import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { Stars } from "../../components/site/Reviews";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3 outline-none focus:border-[#FF1F8E]";
const EMPTY = { name: "", quote: "", rating: 5, product: "" };

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/reviews").then((r) => setItems(r.data)); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("image", file);
    try {
      const { data } = await api.post("/admin/reviews", fd);
      setItems([data, ...items]);
      setForm(EMPTY); setFile(null); e.target.reset();
      toast.success("Review added to your homepage");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Review removed");
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Customer reviews</h1>
      <p className="mt-1 text-sm text-black/55">Reviews show on your homepage (newest 6). The section stays hidden until you add one.</p>
      <form onSubmit={add} className="mt-6 grid gap-4 rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A] md:grid-cols-2" data-testid="admin-review-form">
        <label><span className="mb-1 block text-sm font-semibold">Customer name *</span><input required value={form.name} onChange={set("name")} className={field} placeholder="e.g. Tasha M." data-testid="admin-review-name" /></label>
        <label><span className="mb-1 block text-sm font-semibold">What they ordered</span><input value={form.product} onChange={set("product")} className={field} placeholder="e.g. Graduation stoles" data-testid="admin-review-product" /></label>
        <label className="md:col-span-2"><span className="mb-1 block text-sm font-semibold">Their review *</span><textarea required rows={3} value={form.quote} onChange={set("quote")} className={field} placeholder="Paste their kind words here…" data-testid="admin-review-quote" /></label>
        <div><span className="mb-1 block text-sm font-semibold">Stars</span>
          <div className="flex gap-1" data-testid="admin-review-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} stars`} data-testid={`admin-review-star-${n}`}>
                <Star className={`h-8 w-8 transition-colors ${n <= form.rating ? "fill-[#D4A537] text-[#D4A537]" : "text-[#D4A537]/30"}`} />
              </button>
            ))}
          </div>
        </div>
        <label><span className="mb-1 block text-sm font-semibold">Photo of their order (optional)</span><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="admin-review-file" /></label>
        <button disabled={busy} className="btn-pink !py-3 md:col-span-2 md:justify-self-start disabled:opacity-60" data-testid="admin-review-add-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add review
        </button>
      </form>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-reviews-list">
        {items.map((r) => (
          <div key={r.id} className="overflow-hidden rounded-2xl bg-white ring-1 ring-[#EBD08A]" data-testid={`admin-review-item-${r.id}`}>
            {r.storage_path && <img src={fileUrl(r.storage_path)} alt="" className="aspect-[4/3] w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-semibold">{r.name}</p>{r.product && <p className="text-xs text-[#D4A537]">{r.product}</p>}</div>
                <button onClick={() => remove(r.id)} className="rounded-full p-2 text-black/50 hover:bg-red-50 hover:text-red-600" data-testid={`admin-review-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <Stars n={r.rating} />
              <p className="mt-2 line-clamp-4 text-sm text-black/70">"{r.quote}"</p>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-2xl bg-white p-6 text-sm text-black/50 sm:col-span-2 lg:col-span-3" data-testid="admin-reviews-empty">No reviews yet. Add your first one above!</p>}
      </div>
    </div>
  );
}
